class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    // eslint-disable-next-line no-undef
    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
      // eslint-disable-next-line no-undef
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;
  onVariantChangeUnsubscriber = undefined;

  connectedCallback() {
    // eslint-disable-next-line no-undef
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });

    // eslint-disable-next-line no-undef
    this.onVariantChangeUnsubscriber = subscribe(
      // eslint-disable-next-line no-undef
      PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleOptionValueChange.bind(this),
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

    this.onVariantChangeUnsubscriber?.();
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId,
      );
    }
  }

  onChange(event) {
    if (event.target.type === 'number') {
      this.validateQuantity(event);
    }
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      // eslint-disable-next-line no-undef
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      // eslint-disable-next-line no-undef
      return fetch(`${routes.cart_url}?section_id=main-cart`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          document.querySelector('#main-cart').innerHTML = html.querySelector('#main-cart').innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart',
        section: 'main-cart',
        selector: '#main-cart',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: 'main-cart-order-summary',
        selector: '.cart__order-summary__wrapper',
      },
    ];
  }

  getSectionsToHideOnEmpty() {
    return ['main-cart-checkout-ctas', 'main-cart-purchase-info'];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    this.makeCartRequestAndUpdatePage(
      () =>
        // eslint-disable-next-line no-undef
        fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
          .then((response) => response.text())
          .then((text) => JSON.parse(text)),
      line,
      eventTarget,
      name,
      variantId,
      event,
    );
  }

  async handleOptionValueChange({ data: { event, selectedOptionValues } }) {
    if (!this.contains(event.target)) return;
    const index = event.target.dataset.index;
    const cartItem = this.querySelector(`#CartItem-${index}`);
    const quantity = parseInt(cartItem.querySelector('.quantity__input').value);
    const allOptionGroups = JSON.parse(cartItem.querySelector('variant-selects').dataset.productVariantsOptionIds);
    const variantIndex = allOptionGroups.findIndex((optionGroup) =>
      optionGroup.every((option) => selectedOptionValues.includes(option)),
    );
    const selectedVariant = JSON.parse(cartItem.querySelector('variant-selects').dataset.productVariants)[variantIndex];
    const storefrontConfig = JSON.parse(this.dataset.storefrontConfig);
    // Cart line item variant can only be changed through the GraphQL Storefront API, not through Ajax API
    try {
      // This gets us our cart GID (not available on global cart object), but doesn't provide line item GIDs
      const ajaxCart = JSON.parse(
        // eslint-disable-next-line no-undef
        await fetch('/cart.js', { ...fetchConfig(), method: 'GET' }).then((response) => response.text()),
      );
      const cartToken = ajaxCart.token;
      // This gets us our line item GIDs
      const { data, errors } = await fetch(
        `${storefrontConfig.domain}/api/${storefrontConfig.storefrontApiVersion}/graphql.json`,
        {
          method: 'POST',
          body: JSON.stringify({
            operationName: 'getCart',
            query: `
            query getCart($id: ID!) {
              cart(id: $id) {
                id
                lines(first: 200) {
                  edges {
                    node {
                      id
                       attributes {
                        key
                        value
                      }
                    }
                  }
                }
              }
          }`,
            variables: {
              id: `gid://shopify/Cart/${cartToken}`,
            },
          }),
          headers: {
            'Content-Type': 'application/json',
            'x-shopify-storefront-access-token': storefrontConfig.storefrontApiAccessToken,
          },
        },
      ).then((res) => res.json());
      if (!data || errors?.length) {
        throw new Error(`Failed to request cart from GraphQL API.`);
      }

      const lineGID = data.cart.lines.edges[parseInt(index) - 1].node.id;
      const attributes = data.cart.lines.edges[parseInt(index) - 1].node.attributes;

      this.enableLoading(index);

      // Now with our cart GID and line item GID we can finally actually make the API call to update
      return fetch(`${storefrontConfig.domain}/api/${storefrontConfig.storefrontApiVersion}/graphql.json`, {
        method: 'POST',
        body: JSON.stringify({
          operationName: 'updateLineItems',
          query: `
          fragment VariantFragment on ProductVariant {
            id
            title
          }
          fragment CartLineFragment on BaseCartLine {
            id
            quantity
            attributes {
              key
              value
            }
            merchandise {
              ...VariantFragment
            }
          }
          mutation updateLineItems($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
                    cartLinesUpdate(cartId: $cartId, lines: $lines) {
                        cart {
                            id
                            lines(first: 20) {
                              edges {
                                node {
                                  ...CartLineFragment
                                }
                              }
                            }
                        }
                    }
                }`,
          variables: {
            cartId: `gid://shopify/Cart/${cartToken}`,
            lines: [
              {
                attributes,
                id: lineGID,
                merchandiseId: `gid://shopify/ProductVariant/${selectedVariant}`,
                quantity,
              },
            ],
          },
        }),
        headers: {
          'Content-Type': 'application/JSON',
          'x-shopify-storefront-access-token': storefrontConfig.storefrontApiAccessToken,
        },
      })
        .then((res) => res.json())
        .then(() =>
          this.makeCartRequestAndUpdatePage(
            () =>
              // Now we can retrieve the new cart data
              // eslint-disable-next-line no-undef
              fetch('/cart.js', { ...fetchConfig(), method: 'GET' })
                .then((res) => res.text())
                .then((res) => JSON.parse(res))
                .then(
                  (cart) =>
                    new Promise((resolve, reject) => ({
                      cart,
                      sections: fetch(
                        // The cart GET request cannot return sections, so we also need to request the cart page separately
                        // to update the page sections to reflect our variant change
                        `/cart?sections=${this.getSectionsToRender().map((section) => section.section)}`,
                        {
                          method: 'GET',
                        },
                      )
                        .then((res) => res.text())
                        .then((res) => JSON.parse(res))
                        .then((res) =>
                          resolve({
                            ...cart,
                            sections: res,
                          }),
                        )
                        .catch((err) => reject(err)),
                    })),
                ),
            index,
            'change',
            event.target.name,
            selectedVariant,
            event,
          ),
        )
        .catch(this.handleError);
    } catch (err) {
      this.disableLoading(index);
      this.handleError(err);
    }
  }

  makeCartRequestAndUpdatePage(requestCallback, line, eventTarget, name, variantId, event) {
    requestCallback()
      .then((parsedState) => {
        // eslint-disable-next-line no-undef
        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooters = Array.from(document.getElementsByClassName('main-cart-footer'));

          cartFooters.forEach((footer) => footer.classList.toggle('is-empty', parsedState.item_count === 0));
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector,
            );
          });

          this.getSectionsToHideOnEmpty().forEach((sectionId) => {
            document.getElementById(sectionId)?.classList.toggle('is-empty', parsedState.item_count === 0);
          });

          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? // eslint-disable-next-line no-undef
                trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (
            parsedState.item_count === 0 &&
            cartDrawerWrapper &&
            cartDrawerWrapper.classList.contains('active')
          ) {
            // eslint-disable-next-line no-undef
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            // eslint-disable-next-line no-undef
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        // eslint-disable-next-line no-undef
        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        // eslint-disable-next-line no-undef
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState,
          productVariantId: variantId,
        });
      })
      .catch(this.handleError)
      .finally(() => {
        this.disableLoading(line);
      });
  }

  handleError = (error) => {
    if (window.isLocal || window.isDev) {
      console.error(error.stack);
    }
    this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
    const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    errors.textContent = window.cartStrings.error;
  };

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          // eslint-disable-next-line no-undef
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            // eslint-disable-next-line no-undef
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() =>
              // eslint-disable-next-line no-undef
              CartPerformance.measureFromEvent('note-update:user-action', event),
            );
            // eslint-disable-next-line no-undef
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}
